import { WagmiAdapter } from '@reown/appkit-adapter-wagmi'
import multicall3Abi from 'abis/multicall3.json'
import { networks, pollingInterval, SupportedChainId } from 'config/chains'
import { getRpcUrls } from 'config/rpc'
import { MULTICALL_3_ADDRESSES } from 'constants/addresses'
import { decodeFunctionData, Hex } from 'viem'
import { cookieStorage, createStorage, fallback, http } from 'wagmi'

const projectId = import.meta.env.VITE_REOWN_PROJECT_ID
const LOGGING_INTERVAL = 60000

if (!projectId) {
  throw new Error('Reown Project ID is not defined')
}

// --- Types & state ---

// Function selector to name mapping for common functions
const COMMON_SELECTORS: Record<string, string> = {
  // ERC20
  '0x70a08231': 'balanceOf',
  '0x18160ddd': 'totalSupply',
  '0x95d89b41': 'symbol',
  '0x06fdde03': 'name',
  '0x313ce567': 'decimals',
  '0xdd62ed3e': 'allowance',
  '0x095ea7b3': 'approve',
  '0xa9059cbb': 'transfer',
  '0x23b872dd': 'transferFrom',
  // Uniswap V3 Pool
  '0x3850c7bd': 'slot0',
  '0x1a686502': 'liquidity',
  '0x0dfe1681': 'token0',
  '0xd21220a7': 'token1',
  '0xddca3f43': 'fee',
  // Multicall3
  '0x4d2301cc': 'getEthBalance',
  '0xbce38bd7': 'tryAggregate',
  '0x42cbb15c': 'getBlockNumber',
  '0x0f28c97d': 'getCurrentBlockTimestamp',
  // BGT
  '0x9203ad5d': 'unboostedBalanceOf',
  // ENS Registry
  '0x0178b8bf': 'resolver',
  // Island
  '0x1322d954': 'getUnderlyingBalances',
  '0x9b1344ac': 'lowerTick',
  '0x727dd228': 'upperTick',
  // Reward Vault
  '0x51ed6a30': 'stakeToken',
  '0xf7c618c1': 'rewardToken',
  '0x386a9525': 'rewardsDuration',
  '0xebe2b12b': 'periodFinish',
  '0x7b0a47ee': 'rewardRate',
  '0xa4dbda3c': 'getTotalDelegateStaked',
  // Bault
  '0xce96cb77': 'maxWithdraw',
  '0xef8b30f7': 'previewDeposit',
  '0x01e1d114': 'totalAssets',
  // KodiakFarm
  '0x12edb24c': 'getAllRewardTokens',
  '0x323331ca': 'rewardsCollectionPaused',
  '0x008cc262': 'earned',
  '0x1e090f01': 'lockedStakesOf',
}

function getFunctionName(callData: Hex): string {
  if (callData.length < 10) return 'unknown'
  const selector = callData.slice(0, 10).toLowerCase()
  return COMMON_SELECTORS[selector] || selector
}

interface MulticallRequestLog {
  totalInnerCalls: number
  contracts: Map<string, Map<string, number>> // contract -> fn -> count
  timestamp: number
}

interface DirectCallRequestLog {
  totalCalls: number
  contracts: Map<string, Map<string, number>> // contract -> fn -> count
  timestamp: number
}

interface DecodedCall {
  target: string
  functionName: string
  callData?: Hex
}

interface MethodStats {
  count: number
  firstTs: number
  lastTs: number
}

const multicallRequests: MulticallRequestLog[] = []
let totalInnerMulticallCalls = 0

const directCallRequests: DirectCallRequestLog[] = []
let totalDirectCalls = 0

const nonMulticallMethodStats: Map<string, MethodStats> = new Map()
let lastLogTime = Date.now()
let multicallRequestCount = 0
let nonMulticallRequestCount = 0

// --- Decoding helpers ---

function decodeMulticallData(callData: Hex): DecodedCall[] | null {
  try {
    const decoded = decodeFunctionData({
      abi: multicall3Abi,
      data: callData,
    })

    if (decoded.functionName === 'aggregate3' || decoded.functionName === 'aggregate3Value') {
      const calls = decoded.args?.[0] as Array<{
        target: string
        allowFailure: boolean
        callData: Hex
        value?: bigint
      }>
      return calls.map((call) => ({
        target: call.target.toLowerCase(),
        functionName: getFunctionName(call.callData),
        callData: call.callData,
      }))
    }

    if (decoded.functionName === 'aggregate' || decoded.functionName === 'blockAndAggregate') {
      const calls = decoded.args?.[0] as Array<{ target: string; callData: Hex }>
      return calls.map((call) => ({
        target: call.target.toLowerCase(),
        functionName: getFunctionName(call.callData),
        callData: call.callData,
      }))
    }

    if (decoded.functionName === 'tryAggregate' || decoded.functionName === 'tryBlockAndAggregate') {
      const calls = decoded.args?.[1] as Array<{ target: string; callData: Hex }>
      return calls.map((call) => ({
        target: call.target.toLowerCase(),
        functionName: getFunctionName(call.callData),
        callData: call.callData,
      }))
    }

    return null
  } catch {
    return null
  }
}

function formatTimeWithMs(ts: number | Date) {
  const d = ts instanceof Date ? ts : new Date(ts)
  const pad = (n: number, len = 2) => String(n).padStart(len, '0')

  return (
    `${pad(d.getHours())}:` + `${pad(d.getMinutes())}:` + `${pad(d.getSeconds())}.` + `${pad(d.getMilliseconds(), 3)}`
  )
}

// --- Logging ---

function logMulticallStats() {
  const now = Date.now()
  if (now - lastLogTime < LOGGING_INTERVAL) {
    return
  }

  if (multicallRequests.length === 0 && directCallRequests.length === 0 && nonMulticallMethodStats.size === 0) {
    lastLogTime = now
    return
  }

  const intervalSec = LOGGING_INTERVAL / 1000
  const tsNow = formatTimeWithMs(now) // <-- NEW

  const chainIdStats = nonMulticallMethodStats.get('eth_chainId')
  const blockNumberStats = nonMulticallMethodStats.get('eth_blockNumber')
  const getBlockByNumberStats = nonMulticallMethodStats.get('eth_getBlockByNumber')
  const ethBalanceStats = nonMulticallMethodStats.get('eth_getBalance')

  const chainIdCount = chainIdStats?.count ?? 0
  const blockNumberCount = (blockNumberStats?.count ?? 0) + (getBlockByNumberStats?.count ?? 0)
  const ethBalanceCount = ethBalanceStats?.count ?? 0

  const totalNonMulticall = Array.from(nonMulticallMethodStats.values()).reduce((acc, s) => acc + s.count, 0)
  const otherMethodsCount = totalNonMulticall - chainIdCount - blockNumberCount - ethBalanceCount

  const totalRpcEntries = multicallRequestCount + nonMulticallRequestCount

  console.groupCollapsed(
    `[${tsNow}] [RPC Stats] Total RPCs: ${totalRpcEntries} | Multicall: ${multicallRequestCount} (inner calls: ${totalInnerMulticallCalls}) | eth_call: ${totalDirectCalls} | chainId: ${chainIdCount} | blockNumber: ${blockNumberCount} | ethBalance: ${ethBalanceCount} | other: ${otherMethodsCount} (last ${intervalSec}s)`
  )

  // --- Multicall breakdown: 1 group per RPC request, grouped by contract + fn ---
  if (multicallRequests.length > 0) {
    console.group('Multicall breakdown (per RPC request):')

    multicallRequests.forEach((req, idx) => {
      const ts = formatTimeWithMs(req.timestamp)
      console.group(`#${idx + 1} @ ${ts} innerCalls=${req.totalInnerCalls}`)

      // Build: function -> contract -> count
      const byFunction: Map<string, Map<string, number>> = new Map()

      req.contracts.forEach((fnMap, address) => {
        fnMap.forEach((count, fn) => {
          let contractMap = byFunction.get(fn)
          if (!contractMap) {
            contractMap = new Map()
            byFunction.set(fn, contractMap)
          }
          contractMap.set(address, count)
        })
      })

      // Compute totals per function
      const functionTotals: Map<string, number> = new Map()
      byFunction.forEach((contractMap, fn) => {
        const total = Array.from(contractMap.values()).reduce((a, b) => a + b, 0)
        functionTotals.set(fn, total)
      })

      // Log detail with totals inline
      byFunction.forEach((contractMap, fn) => {
        const total = functionTotals.get(fn) ?? 0
        console.groupCollapsed(`${fn}: ${total}`)

        contractMap.forEach((count, address) => {
          console.log(`${address}: ${count}`)
        })

        console.groupEnd()
      })

      console.groupEnd() // request index
    })

    console.groupEnd()
  }

  // --- Direct eth_call breakdown (non-multicall) ---
  if (directCallRequests.length > 0) {
    console.group('Direct eth_call breakdown (per RPC request):')

    directCallRequests.forEach((req, idx) => {
      const ts = formatTimeWithMs(req.timestamp)
      console.group(`#${idx + 1} @ ${ts} calls=${req.totalCalls}`)

      const byFunction: Map<string, Map<string, number>> = new Map()

      req.contracts.forEach((fnMap, address) => {
        fnMap.forEach((count, fn) => {
          let contractMap = byFunction.get(fn)
          if (!contractMap) {
            contractMap = new Map()
            byFunction.set(fn, contractMap)
          }
          contractMap.set(address, count)
        })
      })

      const functionTotals: Map<string, number> = new Map()
      byFunction.forEach((contractMap, fn) => {
        const total = Array.from(contractMap.values()).reduce((a, b) => a + b, 0)
        functionTotals.set(fn, total)
      })

      byFunction.forEach((contractMap, fn) => {
        const total = functionTotals.get(fn) ?? 0
        console.groupCollapsed(`${fn}: ${total}`)

        contractMap.forEach((count, address) => {
          console.log(`${address}: ${count}`)
        })

        console.groupEnd()
      })

      console.groupEnd()
    })

    console.groupEnd()
  }

  // --- Key methods (with timestamps) ---
  if (chainIdStats || blockNumberStats || getBlockByNumberStats || ethBalanceStats) {
    console.group('Key RPC methods (with timestamps):')
    const logMethod = (name: string, stats?: MethodStats) => {
      if (!stats) return
      const first = formatTimeWithMs(stats.firstTs)
      const last = formatTimeWithMs(stats.lastTs)
      console.log(`${name}: ${stats.count} (first: ${first}, last: ${last})`)
    }

    logMethod('eth_chainId', chainIdStats)
    logMethod('eth_blockNumber', blockNumberStats)
    logMethod('eth_getBlockByNumber', getBlockByNumberStats)
    logMethod('eth_getBalance', ethBalanceStats)

    console.groupEnd()
  }

  // --- Non-multicall "other" methods (with timestamps) ---
  if (nonMulticallMethodStats.size > 0) {
    const otherMethods = Array.from(nonMulticallMethodStats.entries()).filter(
      ([method]) =>
        method !== 'eth_chainId' &&
        method !== 'eth_blockNumber' &&
        method !== 'eth_getBlockByNumber' &&
        method !== 'eth_getBalance'
    )

    if (otherMethods.length > 0) {
      console.group('Other RPC methods (with timestamps):')
      otherMethods.forEach(([method, stats]) => {
        const first = formatTimeWithMs(stats.firstTs)
        const last = formatTimeWithMs(stats.lastTs)
        console.log(`${method}: ${stats.count} (first: ${first}, last: ${last})`)
      })
      console.groupEnd()
    }
  }

  console.groupEnd()

  // Reset window
  multicallRequests.length = 0
  totalInnerMulticallCalls = 0
  directCallRequests.length = 0
  totalDirectCalls = 0
  nonMulticallMethodStats.clear()
  multicallRequestCount = 0
  nonMulticallRequestCount = 0
  lastLogTime = now
}

// --- RPC hook ---

async function handleRpcRequest(request: Request, chainId: SupportedChainId) {
  try {
    const body = await request.clone().json()
    const requests = Array.isArray(body) ? body : [body]
    const multicallAddress = MULTICALL_3_ADDRESSES[chainId].toLowerCase()

    let hasMulticall = false
    let hasDirectCalls = false

    // Per-HTTP direct-call aggregation
    const directContracts: Map<string, Map<string, number>> = new Map()
    let directCallCount = 0

    for (const req of requests) {
      const method = req.method as string | undefined
      const params0 = req.params?.[0]
      const toAddress = params0?.to?.toLowerCase()
      const callData = params0?.data as Hex | undefined

      const isMulticall = !!toAddress && toAddress === multicallAddress && !!callData

      if (isMulticall && method === 'eth_call') {
        // Multicall RPC
        hasMulticall = true
        multicallRequestCount++

        const decodedCalls = decodeMulticallData(callData!)
        if (decodedCalls && decodedCalls.length > 0) {
          // One-level expansion of nested multicalls (e.g. inner tryAggregate from useReadContractMultiple)
          const expandedCalls: DecodedCall[] = []

          for (const call of decodedCalls) {
            // If this inner call is itself a multicall (target = multicallAddress),
            // try to decode its children once.
            if (call.target === multicallAddress && call.callData) {
              const nested = decodeMulticallData(call.callData)
              if (nested && nested.length > 0) {
                expandedCalls.push(...nested)
              } else {
                expandedCalls.push(call)
              }
            } else {
              expandedCalls.push(call)
            }
          }

          totalInnerMulticallCalls += expandedCalls.length

          const perContract: Map<string, Map<string, number>> = new Map()

          for (const call of expandedCalls) {
            let fnMap = perContract.get(call.target)
            if (!fnMap) {
              fnMap = new Map()
              perContract.set(call.target, fnMap)
            }
            fnMap.set(call.functionName, (fnMap.get(call.functionName) ?? 0) + 1)
          }

          multicallRequests.push({
            totalInnerCalls: expandedCalls.length,
            contracts: perContract,
            timestamp: Date.now(),
          })
        }
      } else {
        // Non-multicall RPC
        nonMulticallRequestCount++
        if (method) {
          const nowTs = Date.now()
          const existing = nonMulticallMethodStats.get(method)
          if (!existing) {
            nonMulticallMethodStats.set(method, {
              count: 1,
              firstTs: nowTs,
              lastTs: nowTs,
            })
          } else {
            existing.count += 1
            existing.lastTs = nowTs
          }
        }

        // Direct contract calls via eth_call (non-multicall)
        if (method === 'eth_call' && toAddress && callData) {
          hasDirectCalls = true
          directCallCount += 1

          const fnName = getFunctionName(callData)
          let fnMap = directContracts.get(toAddress)
          if (!fnMap) {
            fnMap = new Map()
            directContracts.set(toAddress, fnMap)
          }
          fnMap.set(fnName, (fnMap.get(fnName) ?? 0) + 1)
        }
      }
    }

    if (hasDirectCalls && directCallCount > 0) {
      totalDirectCalls += directCallCount
      directCallRequests.push({
        totalCalls: directCallCount,
        contracts: directContracts,
        timestamp: Date.now(),
      })
    }

    // Interval-based logging
    logMulticallStats()
  } catch {
    // Ignore parsing errors
  }
}

// --- Transport & adapter ---

const multicallConfig = {
  batchSize: 1024 * 10,
  wait: 30,
}

function createTrackedHttp(url: string, chainId: SupportedChainId) {
  return http(url, {
    batch: {
      ...multicallConfig,
    },
    onFetchRequest: (request) => handleRpcRequest(request, chainId),
  })
}

const transports = {
  [SupportedChainId.BERA]: fallback(
    getRpcUrls(SupportedChainId.BERA).map((url) => createTrackedHttp(url, SupportedChainId.BERA))
  ),
  [SupportedChainId.BEPOLIA]: fallback(
    getRpcUrls(SupportedChainId.BEPOLIA).map((url) => createTrackedHttp(url, SupportedChainId.BEPOLIA))
  ),
  [SupportedChainId.ROBINHOOD]: fallback(
    getRpcUrls(SupportedChainId.ROBINHOOD).map((url) => createTrackedHttp(url, SupportedChainId.ROBINHOOD))
  ),
}

export const wagmiAdapter = new WagmiAdapter({
  storage: createStorage({
    storage: cookieStorage,
  }),
  ssr: false, // Client-side React app, not SSR
  projectId,
  networks,
  transports,
  pollingInterval,
  batch: {
    multicall: multicallConfig,
  },
})
